import { formatDubLevel } from '@src/components/aiVoices/DubLevelSlider';
import Checkbox from '@src/components/common/Checkbox';
import { StepperNumberInput } from '@src/components/common/StepperNumberInput';
import Switch from '@src/components/common/Switch';
import {
  DEFAULT_LEAD_IN_FADE_SEC,
  DEFAULT_LEAD_IN_SEC,
  leadInDubDelaySec,
  leadInOriginalLevel,
  MAX_LEAD_IN_FADE_SEC,
  MAX_LEAD_IN_SEC,
} from '@src/editor/dubbing/leadIn';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import React from 'react';
import styled from 'styled-components';

import type { DubLeadIn } from '@src/editor/dubbing/leadIn';

/** A typed or stepped length, or `null` while the field is empty or wrong. */
function readSeconds(raw: number | string): number | null {
  if (raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

const seconds = (value: number) => `${Math.round(value * 10) / 10}s`;

const PLAYS_FOR_INFO =
  'How long the speaker is heard in their own voice before the dub. Their first words are moved into the pause before they start, so the dub still comes in on its line.';

const DUB_DELAY_INFO =
  'Holds every dubbed line back by this much. Use it when there is not enough pause before the first word and the speaker runs on under the dub.';

/** What the clip's opening sounds like, in one sentence. */
export function describeLeadIn(
  leadIn: DubLeadIn,
  underDubLevel: number,
): string {
  const level = leadInOriginalLevel(leadIn);
  const sentence = describeLeadInTiming(leadIn, underDubLevel);
  return level === 100
    ? sentence
    : `Lead-in at ${formatDubLevel(level)}. ${sentence}`;
}

function describeLeadInTiming(
  leadIn: DubLeadIn,
  underDubLevel: number,
): string {
  const fade = leadIn.fadeSec
    ? `, fading out over its last ${seconds(leadIn.fadeSec)}`
    : '';
  const delaySec = leadInDubDelaySec(leadIn);
  const dub = delaySec
    ? `the dub comes in ${seconds(delaySec)} after its line`
    : 'the dub comes in on its line';
  const under =
    underDubLevel === 0
      ? 'the original is muted'
      : underDubLevel === 100
        ? 'the original stays at full'
        : `the original drops to ${formatDubLevel(underDubLevel)}`;
  return `The speaker is heard for ${seconds(leadIn.originalPlaysSec)}${fade}, then ${dub}; under it ${under}.`;
}

export interface ReviewLeadInPanelProps {
  /** The review's lead-in, or `undefined` when it is off. */
  leadIn: DubLeadIn | undefined;
  /** Heard at once; kept on the clip by the panel's Apply. */
  onChange: (next: DubLeadIn | undefined) => void;
  /** The original's level while the dub plays — the panel's Original. */
  underDubLevel: number;
  /** The host's tooltip, which carries the lead-in's summary. */
  tooltipId: string;
}

/**
 * The original lead-in, in the Mix panel on Both: the speaker is heard first,
 * in their own voice, and the dub comes in on its line. How loud the original
 * is after that is the panel's own Original slider. The dub's delay is the
 * secondary control, for clearing a lead-in that runs on under the dub.
 */
export const ReviewLeadInPanel: React.FC<ReviewLeadInPanelProps> = ({
  leadIn,
  onChange,
  underDubLevel,
  tooltipId,
}) => {
  const fading = !!leadIn?.fadeSec;

  return (
    <Panel data-testid="review-lead-in">
      <div className="toggleRow">
        <Switch
          tiny
          label="Original Lead-In"
          labelColor="var(--foreground)"
          labelSize="var(--text-xs)"
          checked={!!leadIn}
          onClick={() =>
            onChange(
              leadIn ? undefined : { originalPlaysSec: DEFAULT_LEAD_IN_SEC },
            )
          }
        />
        {leadIn && (
          <span
            className="info"
            data-testid="review-lead-in-summary"
            aria-label={describeLeadIn(leadIn, underDubLevel)}
            data-tooltip-id={tooltipId}
            data-tooltip-content={describeLeadIn(leadIn, underDubLevel)}
          >
            <InfoIconSmall />
          </span>
        )}
      </div>
      {leadIn && (
        <>
          <div className="fieldRow">
            <span className="fieldLabel">
              <span className="fieldName" id="review-lead-in-plays">
                Original Plays For
              </span>
              <span
                className="info"
                data-testid="review-lead-in-plays-info"
                aria-label={PLAYS_FOR_INFO}
                data-tooltip-id={tooltipId}
                data-tooltip-content={PLAYS_FOR_INFO}
              >
                <InfoIconSmall />
              </span>
            </span>
            <div className="stepper" aria-labelledby="review-lead-in-plays">
              <StepperNumberInput
                defaultValue={leadIn.originalPlaysSec}
                unit="s"
                min={1}
                max={MAX_LEAD_IN_SEC}
                decrementLabel="Shorter lead-in"
                incrementLabel="Longer lead-in"
                onChange={raw => {
                  const originalPlaysSec = readSeconds(raw);
                  if (originalPlaysSec === null || originalPlaysSec <= 0) {
                    return;
                  }
                  onChange({ ...leadIn, originalPlaysSec });
                }}
              />
            </div>
          </div>
          <div className="fade" data-testid="review-lead-in-fade">
            <span className="fadeToggle">
              <Checkbox
                label="Fade Out Original"
                checked={fading}
                color={fading ? 'var(--accent)' : 'transparent'}
                borderColor={fading ? 'var(--accent)' : 'var(--border)'}
                labelColor="var(--foreground)"
                onClick={() => {
                  const { fadeSec: _fade, ...rest } = leadIn;
                  onChange(
                    fading
                      ? rest
                      : { ...rest, fadeSec: DEFAULT_LEAD_IN_FADE_SEC },
                  );
                }}
              />
            </span>
            {fading && (
              <div className="fieldRow">
                <span className="fieldName" id="review-lead-in-fade-length">
                  Fade Length
                </span>
                <div
                  className="stepper"
                  aria-labelledby="review-lead-in-fade-length"
                >
                  <StepperNumberInput
                    defaultValue={leadIn.fadeSec}
                    unit="s"
                    min={0}
                    max={MAX_LEAD_IN_FADE_SEC}
                    decrementLabel="Shorter fade"
                    incrementLabel="Longer fade"
                    onChange={raw => {
                      const fadeSec = readSeconds(raw);
                      if (fadeSec === null) return;
                      // A fade of nothing is no fade.
                      const { fadeSec: _fade, ...rest } = leadIn;
                      onChange(fadeSec > 0 ? { ...rest, fadeSec } : rest);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="fieldRow">
            <span className="fieldLabel">
              <span className="fieldName" id="review-lead-in-dub-delay">
                Dub Delay
              </span>
              <span
                className="info"
                data-testid="review-lead-in-dub-delay-info"
                aria-label={DUB_DELAY_INFO}
                data-tooltip-id={tooltipId}
                data-tooltip-content={DUB_DELAY_INFO}
              >
                <InfoIconSmall />
              </span>
            </span>
            <div className="stepper" aria-labelledby="review-lead-in-dub-delay">
              <StepperNumberInput
                defaultValue={leadInDubDelaySec(leadIn)}
                unit="s"
                min={0}
                max={MAX_LEAD_IN_SEC}
                decrementLabel="Shorter dub delay"
                incrementLabel="Longer dub delay"
                onChange={raw => {
                  const delaySec = readSeconds(raw);
                  if (delaySec === null) return;
                  // No delay is not written: the dub on its line.
                  const { delaySec: _delay, ...rest } = leadIn;
                  onChange(delaySec > 0 ? { ...rest, delaySec } : rest);
                }}
              />
            </div>
          </div>
        </>
      )}
    </Panel>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border);

  .fieldRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .fieldLabel {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .fieldName {
    color: var(--text-secondary);
    font-weight: var(--font-weight-medium);
  }
  .stepper {
    width: 104px;
    flex-shrink: 0;
  }
  .fade {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .fadeToggle {
    align-self: flex-start;
  }
  .toggleRow {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .info {
    display: inline-flex;
    align-items: center;
    color: var(--text-secondary);
    cursor: help;
  }
`;
